import {
  IUserListApiResponse,
  UserAPIService,
  IUserSingleApiResponse,
  IAnyApiResponse,
  IUserIPSingleApiResponse,
} from "src/api/Maintenance/API_UserService";
import { ISupervisorPermission } from "src/models/Maintenance/SupervisorPermission.models";
import {
  IUserRoleDetail,
  IUser,
  IUserTypeDetail,
  IUpdateUserPassword,
  IUserStoreDetail,
  IUserGroupRoleDetail,
  IValidateUserOTPSecurity,
  IOTPGenerated,
  IUserIP,
} from "src/models/Maintenance/User.models";
import { PaginationParams } from "src/shared/models/PaginationParams.models";
import {
  setUserList,
  setUserToEdit,
  addUser,
  updateUser,
  deleteUser,
  concatListUser,
  updateUserIP,
  addUserIP,
  deleteUserIP,
} from "src/store/Maintenance/slices/userSlice";

const MapIUserRoleDetailFromList = (
  user: IUser,
  roleId: number,
  actionXPageId?: number
) => {
  return {
    roleXUserId: actionXPageId ?? 0,
    roleId: roleId,
    userId: user?.userId ?? 0,
    status: true,
  } as IUserRoleDetail;
};

const MapIUserTypeDetailFromList = (
  user: IUser,
  userTypeId: number,
  actionXPageId?: number
) => {
  return {
    userXUserTypeId: actionXPageId ?? 0,
    userTypeId: userTypeId,
    userId: user?.userId ?? 0,
    status: true,
  } as IUserTypeDetail;
};

const MapIUserStoreDetailFromList = (
  user: IUser,
  storeId: number,
  actionXPageId?: number
) => {
  return {
    userXStoreId: actionXPageId ?? 0,
    storeId: storeId,
    userId: user?.userId ?? 0,
    status: true,
  } as IUserStoreDetail;
};

const MapIMenuGroupItemRoleFromList = (
  user: IUser,
  menuGroupXRoleXMenuItemId: number,
  userXMenuGroupXItemXRoleId?: number
) => {
  return {
    userXMenuGroupXItemXRoleId: userXMenuGroupXItemXRoleId ?? 0,
    userId: user?.userId ?? 0,
    menuGroupXRoleXMenuItemId: menuGroupXRoleXMenuItemId,
    status: true,
  } as IUserGroupRoleDetail;
};

const MapIUserSupervisorPermissionFromList = (
  user: IUser,
  fingerReasonId: number,
  supervisorPermissionId?: number
) => {
  return {
    supervisorPermissionId: supervisorPermissionId ?? 0,
    fingerReasonId: fingerReasonId,
    userId: user?.userId ?? 0,
    status: true,
  } as ISupervisorPermission;
};

const GenerateOTPImage = (info: IValidateUserOTPSecurity) => {
  return new Promise<IOTPGenerated>(async (resolve, reject) => {
    try {
      const response = await UserAPIService.generateOTP(info);
      resolve(response);
    } catch (exception) {
      reject(exception);
    }
  });
};

const SendEmailOTP = (name: string) => {
  return new Promise<IAnyApiResponse>(async (resolve, reject) => {
    try {
      const response = await UserAPIService.sendOTPEmail(name);
      resolve(response);
    } catch (exception) {
      reject(exception);
    }
  });
};

const RemoveOTP = (isEmail: boolean) => {
  return new Promise<IUserSingleApiResponse>(async (resolve, reject) => {
    try {
      const response = await UserAPIService.removeOTP(isEmail);
      resolve(response);
    } catch (exception) {
      reject(exception);
    }
  });
};

const ValidateOTP = (dispatch: any, info: IValidateUserOTPSecurity) => {
  return new Promise<IUserSingleApiResponse>(async (resolve, reject) => {
    try {
      const response = await UserAPIService.validateOTP(info);
      if (response?.success) {
        dispatch(updateUser(response?.data));
      }
      resolve(response);
    } catch (exception) {
      reject(exception);
    }
  });
};

const getUserById = (dispatch: any, id: number) => {
  return new Promise<IUserSingleApiResponse>(async (resolve, reject) => {
    try {
      const response = await UserAPIService.getById(id);
      if (response?.success) {
        dispatch(setUserToEdit(response?.data));
      }
      resolve(response);
    } catch (exception) {
      reject(exception);
    }
  });
};

const ShowUserOnboard = () => {
  return new Promise<IUserSingleApiResponse>(async (resolve, reject) => {
    try {
      const response = await UserAPIService.showOnboard();
      resolve(response);
    } catch (exception) {
      reject(exception);
    }
  });
};

const UpdateUserOnboard = () => {
  return new Promise<IUserSingleApiResponse>(async (resolve, reject) => {
    try {
      const response = await UserAPIService.updateOnboard();
      resolve(response);
    } catch (exception) {
      reject(exception);
    }
  });
};

const getAllUsers = (dispatch: any, pagination?: PaginationParams) => {
  return new Promise<IUserListApiResponse>(async (resolve, reject) => {
    try {
      const response = await UserAPIService.getAll(pagination?.params);
      if (response?.success) {
        if (pagination?.concat) dispatch(concatListUser(response?.data));
        else dispatch(setUserList(response?.data));
      }
      resolve(response);
    } catch (exception) {
      reject(exception);
    }
  });
};

const AddNewUser = (dispatch: any, newItem: IUser) => {
  return new Promise<IUserSingleApiResponse>(async (resolve, reject) => {
    try {
      const response = await UserAPIService.create(newItem);
      if (response.success) {
        dispatch(addUser(response?.data));
      }
      resolve(response);
    } catch (exception) {
      reject(exception);
    }
  });
};

const UpdateUserOnList = (dispatch: any, newItem: IUser) => {
  return new Promise<IUserSingleApiResponse>(async (resolve, reject) => {
    try {
      const response = await UserAPIService.update(newItem);
      if (response.success) {
        dispatch(updateUser(response?.data));
      }
      resolve(response);
    } catch (exception) {
      reject(exception);
    }
  });
};

const UpdateUserIP = (dispatch: any, newItem: IUserIP) => {
  return new Promise<IUserIPSingleApiResponse>(async (resolve, reject) => {
    try {
      const response = await UserAPIService.updateUserIP(newItem);
      if (response.success) {
        dispatch(updateUserIP(response?.data));
      }
      resolve(response);
    } catch (exception) {
      reject(exception);
    }
  });
};

const DeleteUserIP = (dispatch: any, newItem: IUserIP) => {
  return new Promise<IUserIPSingleApiResponse>(async (resolve, reject) => {
    try {
      const toUpdated = { ...newItem };
      toUpdated.status = false;
      const response = await UserAPIService.updateUserIP(toUpdated);
      if (response.success) {
        dispatch(deleteUserIP(response?.data?.userIpAddressId));
      }
      resolve(response);
    } catch (exception) {
      reject(exception);
    }
  });
};

const AddNewUserIP = (dispatch: any, newItem: IUserIP) => {
  return new Promise<IUserIPSingleApiResponse>(async (resolve, reject) => {
    try {
      const response = await UserAPIService.createUserIP(newItem);
      if (response.success) {
        dispatch(addUserIP(response?.data));
      }
      resolve(response);
    } catch (exception) {
      reject(exception);
    }
  });
};

const UpdateJustRoles = (dispatch: any, newItem: IUser) => {
  return new Promise<IUserSingleApiResponse>(async (resolve, reject) => {
    try {
      const response = await UserAPIService.updateJustRoles(newItem);
      if (response.success) {
        dispatch(updateUser(response?.data));
      }
      resolve(response);
    } catch (exception) {
      reject(exception);
    }
  });
};

const DeleteUser = (dispatch: any, newItem: IUser) => {
  return new Promise<IUserSingleApiResponse>(async (resolve, reject) => {
    try {
      const toUpdated = { ...newItem };
      toUpdated.status = false;
      const response = await UserAPIService.update(toUpdated);
      if (response.success) {
        dispatch(deleteUser(response?.data?.userId));
      }
      resolve(response);
    } catch (exception) {
      reject(exception);
    }
  });
};

const ResetUserPassword = (dispatch: any, toUpdated: IUpdateUserPassword) => {
  return new Promise<IUserSingleApiResponse>(async (resolve, reject) => {
    try {
      const response = await UserAPIService.resetPasswordAdmin(toUpdated);
      resolve(response);
    } catch (exception) {
      reject(exception);
    }
  });
};

export {
  MapIUserRoleDetailFromList,
  MapIUserTypeDetailFromList,
  MapIUserStoreDetailFromList,
  MapIUserSupervisorPermissionFromList,
  MapIMenuGroupItemRoleFromList,
  getAllUsers,
  getUserById,
  AddNewUser,
  UpdateUserOnList,
  UpdateJustRoles,
  DeleteUser,
  ResetUserPassword,
  UpdateUserOnboard,
  ShowUserOnboard,
  GenerateOTPImage,
  ValidateOTP,
  SendEmailOTP,
  RemoveOTP,
  UpdateUserIP,
  AddNewUserIP,
  DeleteUserIP,
};
